import { createFileRoute, Link } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useLineBookings } from '@/features/line-app/api';
import { useLineSessionContext } from '@/features/line-app/session-context';
import { Notice } from './line.app.$connectionId';

/**
 * 「予約の確認・変更」 — the customer's own appointments with this shop.
 *
 * Reached from the shop's rich menu, so it identifies the customer the same way
 * the booking screen does rather than asking them to find the confirmation
 * email. Rescheduling and cancelling hand over to the existing manage page,
 * which already knows the shop's rules about notice periods and refunds; a
 * second implementation here would eventually disagree with it.
 */
export const Route = createFileRoute('/line/app/$connectionId/bookings')({
  component: LineBookingsPage,
});

function LineBookingsPage() {
  const { connectionId } = Route.useParams();
  const { t, i18n } = useTranslation(['booking', 'common']);
  // Established once by the layout: both screens share one LINE sign-in.
  const booker = useLineSessionContext();
  const { data, isLoading } = useLineBookings(connectionId, booker.token);

  if (isLoading) return <Notice title={t('common:status.loading')} />;

  const items = data?.items ?? [];

  return (
    <main className="mx-auto max-w-lg px-4 py-5">
      <h1 className="mb-4 text-lg font-semibold">{t('booking:line.myBookings')}</h1>
      {items.length === 0 ? (
        <p className="text-sm text-bb-muted">{t('booking:line.noBookings')}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((b) => {
            const when = DateTime.fromISO(b.start, { zone: b.timezone })
              .setLocale(i18n.language)
              .toLocaleString({ dateStyle: 'full', timeStyle: 'short' });
            return (
              <li key={b.id} className="rounded-lg border border-bb-line bg-bb-surface p-4">
                <p className="text-sm font-medium">{b.eventName}</p>
                <p className="mt-0.5 text-sm">{when}</p>
                <p className="mt-0.5 text-xs text-bb-muted">{b.location}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.joinUrl ? (
                    <a href={b.joinUrl} className="text-sm underline">
                      {t('booking:line.join')}
                    </a>
                  ) : null}
                  {/* The manage page owns the rules: notice periods, refunds,
                      whether this shop allows rescheduling at all. */}
                  {b.manageUrl && (b.canReschedule || b.canCancel) ? (
                    <a href={b.manageUrl} className="text-sm underline">
                      {t('booking:line.manage')}
                    </a>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <Link
        to="/line/app/$connectionId"
        params={{ connectionId }}
        className="mt-5 block text-center text-sm underline"
      >
        {t('booking:line.bookAnother')}
      </Link>
    </main>
  );
}
